/**
 * Localizador Universal (⌘K) — a sobreposição.
 *
 * Spec: `docs/superpowers/specs/2026-08-14-localizador-universal-cmdk-design.md`
 * Desenho: rodada 1, peça 01 (paleta de duas colunas).
 *
 * D1 — LOCALIZADOR PURO: acha e leva. Nenhuma mutação acontece aqui; a ação
 * vive na tela de destino, que já sabe fazê-la com capability, confirmação e
 * audit. Não introduzir POST/PATCH/DELETE neste diretório.
 *
 * D3 — capability decide o que EXISTE, papel decide só a ORDEM. As facetas se
 * auto-gateiam (cada hook checa a própria cap e devolve vazio); o papel só
 * aparece em `orderSections`.
 *
 * A navegação de teclado mora no CAMPO, não nas linhas: as linhas têm
 * `tabIndex={-1}` para que Tab signifique «próxima seção» em vez de tirar o
 * foco do campo. O operador digita e navega sem sair do input.
 *
 * **A REGRA DO ENTER (TL, 14/Ago):** Enter faz o que o painel direito mostra
 * como ação primária. Antes, o painel dizia com cor que o principal era a
 * conversa (botão âmbar «Abrir conversa ativa») e o Enter ia para o cadastro — a
 * paleta se contradizia, e a ação mais frequente custava mais gestos que a menos
 * frequente. Agora a tecla espelha a tela: não há estado escondido, porque o
 * operador VÊ o botão antes de apertar.
 *
 * Corolário que valeu para Operadores também: a primária do card de operador é
 * «Acompanhar», não «abrir o painel de equipe».
 */
import { Search } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '../../hooks/use-auth';
import { useCapability } from '../../hooks/use-capability';
import { usePersonPrimaryTarget } from '../persons/use-person-primary-target';

import { orderSections } from './spotlight-order';
import { bumpUsage, pushRecent, readRecents, readUsage } from './spotlight-recents';
import { SpotlightMessageRow, SpotlightMessageSkeleton } from './SpotlightMessageRow';
import { SpotlightPreview } from './SpotlightPreview';
import { SpotlightRow } from './SpotlightRow';
import { useConfigHits } from './use-config-hits';
import { useConversationIdHit } from './use-conversation-id-hit';
import { useMessageHits, MESSAGE_MIN_CHARS } from './use-message-hits';
import { useOperatorHits } from './use-operator-hits';
import { usePeopleHits } from './use-people-hits';
import { useScreenHits } from './use-screen-hits';

import './spotlight.css';

import type { SpotlightItem, SpotlightSection } from './types';

const SECTION_TITLE = {
  'conversation-id': 'Conversa',
  config: 'Configurações',
  people: 'Pessoas',
  operators: 'Operadores',
  screens: 'Telas e configuração',
  documents: 'Documentos',
  messages: 'Mensagens',
  recents: 'Você abriu recentemente',
} as const;

export function Spotlight({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}): JSX.Element | null {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const canReadPerson = useCapability('person-read');
  const canReadUsers = useCapability('users-read');
  const canSearchContent = useCapability('message-content-search');

  const [term, setTerm] = useState('');
  const [focusIndex, setFocusIndex] = useState(0);
  /**
   * Enter apertado antes de o painel saber a primária. A paleta GUARDA a
   * intenção e navega quando a resposta chega, em vez de mandar o operador para
   * um fallback e fingir que era isso que ele queria.
   *
   * O invariante é: **o Enter nunca navega para lugar diferente da primária do
   * painel.** Ou faz, ou espera. Na prática a janela é o tempo de um request que
   * começou quando a linha entrou em foco — quase sempre já chegou quando a mão
   * alcança o Enter.
   */
  const [pendingKey, setPendingKey] = useState<string | null>(null);

  // Abrir de novo começa em branco: a paleta não guarda o termo da última vez,
  // porque a próxima busca do operador quase nunca é a mesma.
  useEffect(() => {
    if (open) {
      setTerm('');
      setFocusIndex(0);
      setPendingKey(null);
    }
  }, [open]);

  /**
   * A paleta fecha quando a ROTA muda, não só quando ela mesma navega.
   *
   * Defeito que o TL achou usando: clicar «Abrir conversa ativa» ou «Histórico
   * completo» dentro do painel navegava e **deixava a paleta aberta em cima** —
   * a página trocava no fundo. E era mais amplo que os dois botões: o card
   * navega de TRÊS lugares (os dois botões e cada uma das 4 linhas de episódio),
   * e nenhum passa por `openItem`.
   *
   * Fechar por mudança de rota resolve os três de uma vez, e resolve também
   * qualquer navegação que eu venha a adicionar no painel depois — em vez de
   * passar um callback para dentro de `PersonHitCard`, que é compartilhado com a
   * Inbox e não tem nada para fechar.
   *
   * `commit` continua chamando `onClose` explicitamente: quando o destino é a
   * rota em que já se está, a localização não muda e este efeito não dispara.
   */
  const openedAtRef = useRef<string | null>(null);
  useEffect(() => {
    if (!open) {
      openedAtRef.current = null;
      return;
    }
    const here = `${location.pathname}${location.hash}`;
    if (openedAtRef.current === null) {
      openedAtRef.current = here;
      return;
    }
    if (openedAtRef.current !== here) onClose();
  }, [open, location.pathname, location.hash, onClose]);

  const idHits = useConversationIdHit(term);
  const peopleHits = usePeopleHits(term);
  const operatorHits = useOperatorHits(term);
  const screenHits = useScreenHits(term);
  // D5/T13 — o catálogo do hub (seções + ferramentas), local e instantâneo como as telas.
  const configHits = useConfigHits(term);
  // A seção LENTA. Ela é a última a ser montada e a única com `loading`.
  const { items: messageHits, loading: messagesLoading } = useMessageHits(term);

  const showRecents = term.trim().length === 0;

  const recentItems = useMemo<SpotlightItem[]>(
    () =>
      open && showRecents
        ? readRecents().map((r) => ({
            key: r.key,
            section: r.section,
            label: r.label,
            to: r.to,
          }))
        : [],
    [open, showRecents],
  );

  const sections = useMemo<SpotlightSection[]>(() => {
    if (showRecents) {
      // Sem recente nenhum, a seção não existe — quem fala é o estado vazio.
      // Renderizar cabeçalho E estado vazio diria a mesma coisa duas vezes.
      return recentItems.length > 0
        ? [{ id: 'recents', title: SECTION_TITLE.recents, items: recentItems }]
        : [];
    }
    const built: SpotlightSection[] = [
      { id: 'conversation-id', title: SECTION_TITLE['conversation-id'], items: idHits },
      { id: 'config', title: SECTION_TITLE.config, items: configHits },
      { id: 'people', title: SECTION_TITLE.people, items: peopleHits },
      { id: 'operators', title: SECTION_TITLE.operators, items: operatorHits },
      { id: 'screens', title: SECTION_TITLE.screens, items: screenHits },
      {
        id: 'messages',
        title: SECTION_TITLE.messages,
        items: messageHits,
        // `loading` mantém a seção viva com zero item, para ela RESERVAR a
        // altura que vai ter — `orderSections` preserva seção vazia carregando.
        loading: messagesLoading,
      },
    ];
    return orderSections(built, user?.primaryRole, readUsage());
  }, [
    showRecents,
    recentItems,
    idHits,
    configHits,
    peopleHits,
    operatorHits,
    screenHits,
    // Sem estes dois, a seção lenta NUNCA mostraria o resultado: o memo não
    // recomputaria quando os dados chegassem. Hoje «funciona» só porque outra
    // dependência troca de identidade a cada render — defeito mascarado, não
    // ausente. Pego pelo react-hooks/exhaustive-deps.
    messageHits,
    messagesLoading,
    user?.primaryRole,
  ]);

  const flat = useMemo(() => sections.flatMap((s) => s.items), [sections]);
  const flatKey = flat.map((i) => i.key).join('|');

  // Resultado novo devolve o foco ao primeiro item — senão o índice antigo
  // aponta para outra coisa e o Enter abre o que o operador não escolheu.
  useEffect(() => {
    setFocusIndex(0);
  }, [flatKey]);

  // Calculados ANTES do early return: o hook do destino é chamado
  // incondicionalmente.
  const focused: SpotlightItem | undefined = flat[focusIndex];
  const previewRef = focused?.preview;
  const focusedPersonId = previewRef?.kind === 'person' ? previewRef.person.id : undefined;

  // A primária do painel para a pessoa em foco. Mesma `queryKey` que o card
  // usa, então o React Query dedupa — zero request novo.
  const personTarget = usePersonPrimaryTarget(focusedPersonId, open);

  const commit = useCallback(
    (item: SpotlightItem, to: string): void => {
      pushRecent(item, Date.now());
      bumpUsage(item.section);
      navigate(to);
      onClose();
    },
    [navigate, onClose],
  );

  // Resolve a intenção pendente quando a primária fica conhecida.
  useEffect(() => {
    if (pendingKey === null) return;
    if (focused?.key !== pendingKey) {
      // O foco mudou enquanto esperava: a intenção não vale mais.
      setPendingKey(null);
      return;
    }
    if (personTarget.to !== undefined) {
      setPendingKey(null);
      commit(focused, personTarget.to);
    }
  }, [pendingKey, focused, personTarget.to, commit]);

  if (!open) return null;

  const openItem = (item: SpotlightItem): void => {
    // Pessoa é o único item cuja primária não está na linha: ela depende de
    // existir conversa ativa, e só o servidor sabe. As outras facetas já
    // carregam a primária no próprio `to`.
    if (item.preview?.kind === 'person') {
      if (personTarget.to === undefined) {
        setPendingKey(item.key);
        return;
      }
      commit(item, personTarget.to);
      return;
    }
    commit(item, item.to);
  };

  /**
   * Índice do primeiro item da seção seguinte, ou `null` quando não há próxima.
   *
   * `null` importa: antes isto devolvia 0, então Tab numa lista de uma seção só
   * jogava o foco de volta para a primeira linha — surpresa gratuita. Sem
   * próxima seção, Tab não faz nada.
   */
  const nextSectionIndex = (): number | null => {
    if (focused === undefined) return null;
    const at = sections.findIndex((s) => s.id === focused.section);
    for (let i = at + 1; i < sections.length; i += 1) {
      const first = sections[i]?.items[0];
      if (first !== undefined) return flat.findIndex((x) => x.key === first.key);
    }
    return null;
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusIndex((i) => Math.min(i + 1, Math.max(flat.length - 1, 0)));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusIndex((i) => Math.max(i - 1, 0));
      return;
    }
    if (e.key === 'Tab') {
      e.preventDefault();
      const next = nextSectionIndex();
      if (next !== null) setFocusIndex(next);
      return;
    }
    if (e.key === 'Enter' && focused !== undefined) {
      e.preventDefault();
      openItem(focused);
    }
  };

  const hasResult = flat.length > 0;
  const termLength = term.trim().length;

  return (
    <>
      <div className="spotlight__scrim" onClick={onClose} aria-hidden />
      <div className="spotlight__palette" role="dialog" aria-modal="true" aria-label="Buscar">
        <div className="spotlight__field">
          <span className="spotlight__field-icon">
            <Search size={18} aria-hidden />
          </span>
          <input
            type="search"
            className="spotlight__input"
            placeholder="Pessoa, conversa, tela ou algo que foi dito"
            aria-label="Termo de busca"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            onKeyDown={onKeyDown}
            // A paleta abre por atalho: o campo tem que já estar pronto para
            // receber a primeira tecla. Autofoco aqui é o oposto de armadilha de
            // acessibilidade — sem ele o operador digitaria no vazio.
            autoFocus
          />
          <span className="spotlight__kbd">esc</span>
        </div>

        <div className="spotlight__body">
          <div
            className={`spotlight__results${previewRef === undefined ? ' spotlight__results--wide' : ''}`}
          >
            {!hasResult && !showRecents && (
              <EmptyState
                term={term}
                canReadPerson={canReadPerson}
                canReadUsers={canReadUsers}
                canSearchContent={canSearchContent}
              />
            )}
            {!hasResult && showRecents && (
              <div className="spotlight__empty">
                <div className="spotlight__empty-title">Nada aberto por aqui ainda</div>
                <div className="spotlight__empty-hint">
                  Digite para buscar pessoa, tela ou conversa
                </div>
              </div>
            )}
            {sections.map((section) => (
              <div key={section.id}>
                <div className="spotlight__section">
                  <span data-testid="spotlight-section-title">{section.title}</span>
                  {section.loading === true && section.items.length === 0 ? (
                    // Sem contagem enquanto busca: número que muda é pior que
                    // nenhum número.
                    <span className="spotlight__section-state">buscando</span>
                  ) : (
                    section.items.length > 0 && (
                      <span className="spotlight__section-count">{section.items.length}</span>
                    )
                  )}
                </div>
                {section.id === 'messages' &&
                  section.items.length === 0 &&
                  section.loading === true && <SpotlightMessageSkeleton />}
                {section.items.map((item) =>
                  item.section === 'messages' ? (
                    <SpotlightMessageRow
                      key={item.key}
                      item={item}
                      focused={focused?.key === item.key}
                      onOpen={() => openItem(item)}
                      term={term}
                    />
                  ) : (
                    <SpotlightRow
                      key={item.key}
                      item={item}
                      focused={focused?.key === item.key}
                      onOpen={() => openItem(item)}
                      // Recentes não têm termo para realçar (a paleta está vazia).
                      term={showRecents ? '' : term}
                    />
                  ),
                )}
              </div>
            ))}
          </div>

          {previewRef !== undefined && <SpotlightPreview previewRef={previewRef} />}
        </div>

        <div className="spotlight__foot">
          <span className="spotlight__legend">
            <span className="spotlight__kbd">↑ ↓</span> navegar
          </span>
          <span className="spotlight__legend">
            <span className="spotlight__kbd">enter</span> abrir
          </span>
          <span className="spotlight__legend">
            <span className="spotlight__kbd">tab</span> próxima seção
          </span>
          {/* O rodapé TROCA de conteúdo com o estado — não é legenda fixa. Sem
              esta frase o operador conclui que a Lume não busca conteúdo e nunca
              mais tenta; com ela, a seção que aparece no terceiro caractere vira
              cumprimento de promessa, não surpresa. */}
          {canSearchContent && termLength > 0 && termLength < MESSAGE_MIN_CHARS && (
            <>
              <span className="spotlight__legend" style={{ flex: 1 }} />
              <span className="spotlight__legend" data-testid="spotlight-threshold-hint">
                Conteúdo de mensagem entra com {MESSAGE_MIN_CHARS} caracteres
              </span>
            </>
          )}
        </div>
      </div>
    </>
  );
}

/**
 * O vazio diz O QUE FOI OLHADO — «nada encontrado» sozinho deixa a dúvida mais
 * cara de todas: não existe, ou a busca não procura ali? E enumera só as
 * facetas que ESTE operador tem: quem não tem `person-read` não lê «pessoas».
 */
function EmptyState({
  term,
  canReadPerson,
  canReadUsers,
  canSearchContent,
}: {
  term: string;
  canReadPerson: boolean;
  canReadUsers: boolean;
  canSearchContent: boolean;
}): JSX.Element {
  const consulted = [
    ...(canReadPerson ? ['pessoas'] : []),
    ...(canReadUsers ? ['operadores'] : []),
    'telas e configuração',
    ...(canSearchContent ? ['o conteúdo das conversas que você pode ver'] : []),
  ];
  const last = consulted[consulted.length - 1] ?? '';
  const head = consulted.slice(0, -1);
  const list = head.length > 0 ? `${head.join(', ')} e ${last}` : last;
  return (
    <div className="spotlight__empty">
      <div className="spotlight__empty-title">Nada encontrado para “{term}”</div>
      <div className="spotlight__empty-hint">Foram consultadas {list}.</div>
    </div>
  );
}
